import React, { useEffect } from 'react';

const APP_STORE_URL = 'https://apps.apple.com/app/checkstall-import/id6758943545';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.checkstall.client';

const getMobileStoreUrl = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isIOS = /iPad|iPhone|iPod/i.test(userAgent) || isTouchMac;
  const isAndroid = /Android/i.test(userAgent);
  const isMobile =
    isIOS ||
    isAndroid ||
    navigator.userAgentData?.mobile ||
    /webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  if (!isMobile) {
    return null;
  }

  if (isIOS) {
    return APP_STORE_URL;
  }

  if (isAndroid) {
    return PLAY_STORE_URL;
  }

  return null;
};

const Downloads = () => {
  useEffect(() => {
    const storeUrl = getMobileStoreUrl();

    if (storeUrl) {
      window.location.replace(storeUrl);
    }
  }, []);

  const downloadLinks = [
    {
      platform: 'Android',
      icon: '🤖',
      description: 'Download Checkstall Import for Android devices',
      features: [
        'Full access to product catalog',
        'Secure payment processing',
        'Real-time order tracking',
        'Direct communication with suppliers'
      ],
      playStoreUrl: PLAY_STORE_URL,
      //apkUrl: 'https://downloads.checkstall.com/checkstall-import-latest.apk',
      size: '25.4 MB',
      version: '1.0.8',
      requirements: 'Android 6.0 or higher'
    },
    {
      platform: 'iOS',
      icon: '🍎',
      description: 'Download Checkstall Import for iPhone and iPad',
      features: [
        'Optimized for iOS devices',
        'Touch ID and Face ID support',
        'Seamless sync across devices',
        'Offline browsing capabilities'
      ],
      appStoreUrl: APP_STORE_URL,
      size: '28.7 MB',
      version: '1.0.8',
      requirements: 'iOS 12.0 or higher'
    }
  ];

  const additionalResources = [
    {
      title: 'Main Website',
      description: 'Visit our main platform for web-based access',
      url: 'https://checkstall.com',
      icon: '🌐'
    },
    {
      title: 'Web App',
      description: 'Access Checkstall Import directly in your browser',
      url: 'https://app.checkstall.com',
      icon: '💻'
    },
    {
      title: 'API Documentation',
      description: 'Developer resources and API integration guides',
      url: '#',
      icon: '📚'
    },
    {
      title: 'Support Center',
      description: 'Get help and find answers to common questions',
      url: '#',
      icon: '🆘'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Download <span className="text-primary">Checkstall</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Get the Checkstall Import app on your device and start your international trade journey today
            </p>
          </div>
        </div>
      </section>

      {/* Download Apps Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Mobile Apps</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Download our mobile app for the best experience on your smartphone or tablet
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {downloadLinks.map((app, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-soft-lg p-8 hover:shadow-soft-lg transition-shadow duration-300">
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">{app.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{app.platform}</h3>
                  <p className="text-gray-600 mb-4">{app.description}</p>
                  
                  <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mb-6">
                    <span>Version: {app.version}</span>
                    <span>Size: {app.size}</span>
                    <span>{app.requirements}</span>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Features</h4>
                  <ul className="space-y-2">
                    {app.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <a
                    href={app.playStoreUrl || app.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-primary text-white py-3 px-6 rounded-2xl font-semibold hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      {app.platform === 'Android' ? (
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      ) : (
                        <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                      )}
                    </svg>
                    Download from {app.platform === 'Android' ? 'Google Play' : 'App Store'}
                  </a>
                  
                  {app.apkUrl && (
                    <a
                      href={app.apkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full border-2 border-primary text-primary py-3 px-6 rounded-2xl font-semibold hover:bg-primary hover:text-white transition-colors duration-200 flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download APK
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Additional Resources</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access Checkstall through other platforms and get additional support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalResources.map((resource, index) => (
              <a
                key={index}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-50 rounded-2xl p-6 hover:bg-primary hover:text-white transition-all duration-300 group"
              >
                <div className="text-center">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {resource.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{resource.title}</h3>
                  <p className="text-sm opacity-75">{resource.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">System Requirements</h2>
            <p className="text-xl text-gray-600">
              Make sure your device meets the minimum requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">🤖</span>
                Android Requirements
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Android 6.0 (API level 23) or higher</li>
                <li>• 2GB RAM minimum (4GB recommended)</li>
                <li>• 100MB available storage space</li>
                <li>• Internet connection (Wi-Fi or mobile data)</li>
                <li>• Camera for product scanning</li>
                <li>• GPS for location services</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-soft p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">🍎</span>
                iOS Requirements
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li>• iOS 12.0 or higher</li>
                <li>• iPhone 6s or newer</li>
                <li>• 100MB available storage space</li>
                <li>• Internet connection (Wi-Fi or mobile data)</li>
                <li>• Camera for product scanning</li>
                <li>• Touch ID or Face ID (optional)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Help?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            Having trouble downloading or installing the app? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-primary px-8 py-3 rounded-2xl font-semibold hover:bg-gray-50 transition-colors duration-200"
            >
              Contact Support
            </a>
            <a
              href="mailto:support@checkstall.com"
              className="border-2 border-white text-white px-8 py-3 rounded-2xl font-semibold hover:bg-white hover:text-primary transition-colors duration-200"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Downloads;


